/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBooleanDecoder,
  getBooleanEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type EncodedAccount,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type ReadonlyUint8Array,
} from '@solana/kit';

export const ENTRY_DISCRIMINATOR = new Uint8Array([
  63, 18, 152, 113, 215, 246, 221, 250,
]);

export function getEntryDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(ENTRY_DISCRIMINATOR);
}

export type Entry = {
  discriminator: ReadonlyUint8Array;
  /** The market this entry belongs to */
  market: Address;
  /** Unique identifier for this entry within the market */
  entryId: ReadonlyUint8Array;
  /** The token mint for this entry */
  baseMint: Address;
  /** Quote contributed when this entry was migrated */
  contribution: bigint;
  /** Whether this entry has been migrated */
  isMigrated: boolean;
  /** Bump seed for this PDA */
  bump: number;
  /** Reserved for future use */
  reserved: ReadonlyUint8Array;
};

export type EntryArgs = {
  /** The market this entry belongs to */
  market: Address;
  /** Unique identifier for this entry within the market */
  entryId: ReadonlyUint8Array;
  /** The token mint for this entry */
  baseMint: Address;
  /** Quote contributed when this entry was migrated */
  contribution: number | bigint;
  /** Whether this entry has been migrated */
  isMigrated: boolean;
  /** Bump seed for this PDA */
  bump: number;
  /** Reserved for future use */
  reserved: ReadonlyUint8Array;
};

/** Gets the encoder for {@link EntryArgs} account data. */
export function getEntryEncoder(): FixedSizeEncoder<EntryArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['market', getAddressEncoder()],
      ['entryId', fixEncoderSize(getBytesEncoder(), 32)],
      ['baseMint', getAddressEncoder()],
      ['contribution', getU64Encoder()],
      ['isMigrated', getBooleanEncoder()],
      ['bump', getU8Encoder()],
      ['reserved', fixEncoderSize(getBytesEncoder(), 14)],
    ]),
    (value) => ({ ...value, discriminator: ENTRY_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link Entry} account data. */
export function getEntryDecoder(): FixedSizeDecoder<Entry> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['market', getAddressDecoder()],
    ['entryId', fixDecoderSize(getBytesDecoder(), 32)],
    ['baseMint', getAddressDecoder()],
    ['contribution', getU64Decoder()],
    ['isMigrated', getBooleanDecoder()],
    ['bump', getU8Decoder()],
    ['reserved', fixDecoderSize(getBytesDecoder(), 14)],
  ]);
}

/** Gets the codec for {@link Entry} account data. */
export function getEntryCodec(): FixedSizeCodec<EntryArgs, Entry> {
  return combineCodec(getEntryEncoder(), getEntryDecoder());
}

export function decodeEntry<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<Entry, TAddress>;
export function decodeEntry<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<Entry, TAddress>;
export function decodeEntry<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
): Account<Entry, TAddress> | MaybeAccount<Entry, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getEntryDecoder(),
  );
}

export async function fetchEntry<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<Entry, TAddress>> {
  const maybeAccount = await fetchMaybeEntry(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeEntry<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<Entry, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeEntry(maybeAccount);
}

export async function fetchAllEntry(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<Entry>[]> {
  const maybeAccounts = await fetchAllMaybeEntry(rpc, addresses, config);
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeEntry(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<Entry>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) => decodeEntry(maybeAccount));
}

export function getEntrySize(): number {
  return 128;
}
